import { useState } from 'react'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  BadgeCheck,
  Bookmark,
  BookmarkCheck,
  Briefcase,
  Building2,
  CalendarDays,
  Clock,
  MapPin,
  Users,
} from 'lucide-react'

import { ShareButton } from '@/components/share'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ApplicationStatusBadge,
  VacancyStatusBadge,
  arrangementLabel,
  employmentLabel,
  experienceLabel,
} from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useSession } from '@/features/auth/api'
import { salaryLabel, useApply, useSaveVacancy, useVacancy } from '@/features/hiring/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatNumber, formatRelative, formatRupiah } from '@/lib/format'
import type { ApplicationState } from '@/lib/types'

const route = getRouteApi('/public/lowongan/$vacancyId')

export function VacancyDetailPage() {
  const { vacancyId } = route.useParams()
  const { data, isLoading } = useVacancy(vacancyId)
  const { data: session } = useSession()
  const [applyOpen, setApplyOpen] = useState(false)
  const saveVacancy = useSaveVacancy()
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Lowongan tidak ditemukan"
          description="Lowongan mungkin sudah ditutup perusahaan."
          action={
            <Link to="/lowongan">
              <Button variant="outline">Kembali ke daftar lowongan</Button>
            </Link>
          }
        />
      </div>
    )
  }

  const { vacancy, is_manager: isManager } = data
  const applied = Boolean(vacancy.my_application_state)

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <Link
        to="/lowongan"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Lowongan kerja
      </Link>

      <div className="mt-4 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-2">
                <VacancyStatusBadge status={vacancy.status} />
                <Badge tone="brand">{employmentLabel[vacancy.employment_type]}</Badge>
                <Badge>{arrangementLabel[vacancy.work_arrangement]}</Badge>
                <Badge>{experienceLabel[vacancy.experience_level]}</Badge>
              </div>

              <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
                {vacancy.title}
              </h1>

              <div className="mt-4">
                <ShareButton
                  title={vacancy.title}
                  text={`${vacancy.title} di ${vacancy.company.name}`}
                  label="Bagikan lowongan"
                />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted">
                <span className="inline-flex items-center gap-1.5 font-semibold text-ink">
                  {vacancy.company.name}
                  {vacancy.company.verified ? <BadgeCheck className="size-4 text-brand" /> : null}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="size-4" />
                  {vacancy.city || arrangementLabel[vacancy.work_arrangement]}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Users className="size-4" />
                  {formatNumber(vacancy.application_count)} pelamar
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="size-4" />
                  Diposting {formatRelative(vacancy.published_at ?? vacancy.created_at)}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {vacancy.skills.map((skill) => (
                  <span key={skill} className="rounded-full bg-sunken px-3 py-1 text-sm font-medium text-muted">
                    {skill}
                  </span>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Uraian pekerjaan" />
            <CardBody>
              <p className="whitespace-pre-line leading-relaxed text-muted">{vacancy.description}</p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Kualifikasi" />
            <CardBody>
              <p className="whitespace-pre-line leading-relaxed text-muted">{vacancy.requirements}</p>
            </CardBody>
          </Card>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:h-fit">
          <Card>
            <CardBody className="space-y-4">
              {vacancy.salary_visible && vacancy.salary_max ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-subtle">{salaryLabel(vacancy.employment_type)}</p>
                  <p className="mt-1 text-xl font-extrabold text-ink">
                    {formatRupiah(vacancy.salary_min ?? 0)} – {formatRupiah(vacancy.salary_max)}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-xs uppercase tracking-wide text-subtle">Gaji</p>
                  <p className="mt-1 text-base font-semibold text-ink">Dirundingkan saat proses seleksi</p>
                </div>
              )}

              <dl className="space-y-2 border-t border-line pt-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Posisi dibuka</dt>
                  <dd className="font-semibold text-ink">{vacancy.openings} orang</dd>
                </div>
                {vacancy.closes_at ? (
                  <div className="flex justify-between">
                    <dt className="text-muted">Ditutup</dt>
                    <dd className="font-semibold text-ink">{formatDate(vacancy.closes_at)}</dd>
                  </div>
                ) : null}
              </dl>

              {applied ? (
                <div className="rounded-xl bg-success-soft p-4">
                  <p className="text-sm font-bold text-ink">Anda sudah melamar</p>
                  <div className="mt-2">
                    <ApplicationStatusBadge state={vacancy.my_application_state as ApplicationState} />
                  </div>
                  <Link to="/app/lamaran" className="mt-3 inline-block">
                    <Button size="sm" variant="secondary">
                      Pantau lamaran
                    </Button>
                  </Link>
                </div>
              ) : isManager ? (
                <Link to="/app/perusahaan/lowongan/$vacancyId" params={{ vacancyId: vacancy.id }}>
                  <Button fullWidth size="lg">
                    Kelola pelamar
                  </Button>
                </Link>
              ) : vacancy.status === 'open' ? (
                <div className="space-y-2">
                  <Button
                    fullWidth
                    size="lg"
                    onClick={() => {
                      if (!session) {
                        navigate({ to: '/masuk', search: { redirect: `/lowongan/${vacancy.id}` } })
                        return
                      }
                      setApplyOpen(true)
                    }}
                  >
                    <Briefcase className="size-4.5" />
                    Lamar posisi ini
                  </Button>

                  {session ? (
                    <Button
                      fullWidth
                      variant="outline"
                      loading={saveVacancy.isPending}
                      onClick={() => saveVacancy.mutate({ vacancyId: vacancy.id, saved: vacancy.saved })}
                    >
                      {vacancy.saved ? <BookmarkCheck className="size-4" /> : <Bookmark className="size-4" />}
                      {vacancy.saved ? 'Tersimpan' : 'Simpan lowongan'}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <p className="rounded-xl bg-sunken p-4 text-sm text-muted">
                  Lowongan ini sudah tidak menerima lamaran.
                </p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Tentang perusahaan" />
            <CardBody>
              <div className="flex items-center gap-3">
                {vacancy.company.logo_url ? (
                  <img
                    src={vacancy.company.logo_url}
                    alt=""
                    className="size-12 rounded-xl border border-line object-cover"
                  />
                ) : (
                  <span className="flex size-12 items-center justify-center rounded-xl bg-brand-soft text-brand">
                    <Building2 className="size-6" />
                  </span>
                )}
                <div>
                  <p className="font-bold text-ink">{vacancy.company.name}</p>
                  {vacancy.company.verified ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand">
                      <BadgeCheck className="size-3.5" />
                      Terverifikasi Skava
                    </span>
                  ) : (
                    <span className="text-xs text-subtle">Belum terverifikasi</span>
                  )}
                </div>
              </div>
              <Link
                to="/perusahaan/$slug"
                params={{ slug: vacancy.company.slug }}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
              >
                Lihat profil perusahaan
              </Link>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex gap-3 text-sm text-muted">
              <CalendarDays className="mt-0.5 size-4 shrink-0 text-brand" />
              <p>
                Setiap perpindahan tahap seleksi tercatat dan dapat Anda lihat, dari lamaran terkirim sampai
                keputusan akhir.
              </p>
            </CardBody>
          </Card>
        </aside>
      </div>

      <ApplyDialog vacancyId={vacancy.id} open={applyOpen} onClose={() => setApplyOpen(false)} />
    </div>
  )
}

function ApplyDialog({
  vacancyId,
  open,
  onClose,
}: {
  vacancyId: string
  open: boolean
  onClose: () => void
}) {
  const apply = useApply(vacancyId)
  const toast = useToast()
  const [coverLetter, setCoverLetter] = useState('')
  const [expectedSalary, setExpectedSalary] = useState(0)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kirim lamaran"
      description="Ceritakan singkat mengapa Anda cocok untuk posisi ini."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={apply.isPending}
            disabled={coverLetter.trim().length < 30}
            onClick={() =>
              apply.mutate(
                {
                  cover_letter: coverLetter,
                  expected_salary: expectedSalary > 0 ? expectedSalary : undefined,
                },
                {
                  onSuccess: () => {
                    toast.success('Lamaran terkirim', 'Anda dapat memantau tahapnya di menu Lamaran saya.')
                    setCoverLetter('')
                    onClose()
                  },
                  onError: (error) =>
                    toast.error('Gagal mengirim lamaran', error instanceof ApiError ? error.message : undefined),
                },
              )
            }
          >
            Kirim lamaran
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Surat lamaran"
          rows={7}
          value={coverLetter}
          onChange={(event) => setCoverLetter(event.target.value)}
          placeholder="Pengalaman yang relevan, hal yang ingin Anda kerjakan di posisi ini, dan kapan Anda dapat mulai."
          hint="Minimal 30 karakter. Sebutkan hal konkret — pengalaman spesifik lebih menarik daripada kalimat umum."
        />

        <Input
          label="Ekspektasi gaji per bulan"
          type="number"
          min={0}
          step={500000}
          prefix="Rp"
          value={expectedSalary || ''}
          onChange={(event) => setExpectedSalary(Number(event.target.value))}
          hint="Boleh dikosongkan bila Anda ingin membicarakannya saat wawancara."
        />

        <p className="text-xs leading-relaxed text-subtle">
          Profil talenta Anda ikut terlihat perekrut. Lengkapi pengalaman dan portofolio di halaman profil
          agar lamaran lebih meyakinkan.
        </p>
      </div>
    </Dialog>
  )
}
